import { useState, useEffect } from 'react';
import { DollarSign, Save, RefreshCw } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function BankCommissionsTable({ setToast }) {
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);

  const loadCommissions = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/super-admin/bank-commissions`);
      setCommissions(res.data.commissions || []);
    } catch (e) {
      console.error('Error loading commissions:', e);
    } finally { setLoading(false); }
  };

  useEffect(() => { loadCommissions(); }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/super-admin/bank-commissions`, commissions);
      setToast?.({ type: 'success', message: 'Comisiones bancarias actualizadas' });
      setEditing(null);
    } catch (e) {
      setToast?.({ type: 'error', message: 'Error al guardar comisiones' });
    } finally { setSaving(false); }
  };

  const updateRate = (index, newRate) => {
    const updated = [...commissions];
    updated[index] = { ...updated[index], rate: parseFloat(newRate) || 0 };
    setCommissions(updated);
  };

  const updateNotes = (index, newNotes) => {
    const updated = [...commissions];
    updated[index] = { ...updated[index], notes: newNotes };
    setCommissions(updated);
  };

  if (loading) return <div className="text-center py-4 text-gray-400">Cargando comisiones...</div>;

  return (
    <div data-testid="bank-commissions-table" className="bg-white rounded-xl border">
      <div className="px-5 py-4 border-b flex items-center justify-between">
        <div className="flex items-center gap-2">
          <DollarSign className="w-5 h-5" style={{ color: '#003594' }} />
          <div>
            <h3 className="text-sm font-bold text-gray-800">Comisiones Banco Plaza</h3>
            <p className="text-xs text-gray-500">Tasas cobradas por el banco por cada servicio</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={loadCommissions} className="px-3 py-1.5 text-xs border rounded-lg hover:bg-gray-50" title="Recargar">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
          {editing !== null && (
            <button onClick={handleSave} disabled={saving} className="flex items-center gap-1 px-3 py-1.5 text-xs text-white rounded-lg disabled:opacity-50" style={{ backgroundColor: '#003594' }}>
              <Save className="w-3.5 h-3.5" /> {saving ? 'Guardando...' : 'Guardar'}
            </button>
          )}
        </div>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase">Servicio</th>
            <th className="px-5 py-3 text-center text-xs font-medium text-gray-500 uppercase w-32">Comision %</th>
            <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notas</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {commissions.map((c, idx) => (
            <tr key={c.service} className="hover:bg-gray-50">
              <td className="px-5 py-3">
                <div className="font-medium text-gray-800">{c.label}</div>
                <div className="text-xs text-gray-400 font-mono">{c.service}</div>
              </td>
              <td className="px-5 py-3 text-center">
                <input
                  type="number"
                  step="0.01"
                  value={c.rate}
                  onChange={(e) => { updateRate(idx, e.target.value); setEditing(idx); }}
                  className="w-20 px-2 py-1 text-center border rounded text-sm font-mono font-bold focus:ring-2 focus:ring-blue-500"
                  style={{ color: c.rate > 0 ? '#dc2626' : '#16a34a' }}
                />
                <span className="text-xs text-gray-400 ml-1">%</span>
              </td>
              <td className="px-5 py-3">
                <input
                  type="text"
                  value={c.notes || ''}
                  onChange={(e) => { updateNotes(idx, e.target.value); setEditing(idx); }}
                  className="w-full px-2 py-1 border rounded text-xs text-gray-600 focus:ring-2 focus:ring-blue-500"
                  placeholder="Notas..."
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="px-5 py-3 bg-gray-50 border-t text-xs text-gray-500">
        Estas comisiones son cobradas por Banco Plaza. Se descuentan automaticamente del saldo de la cuenta operativa.
      </div>
    </div>
  );
}
